import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import BadgeTier from "../components/BadgeTier";
import api, { BACKEND_URL, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Crown, Rocket, Sparkles, Wallet, Trophy, Camera } from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";

const CATEGORY_META = {
  creator_of_the_month: { icon: Crown, gradient: "from-[#FF107A] via-[#9D4EDD] to-[#00D4FF]", tone: "text-[#FFEA00]", label: "Creator of the Month" },
  fastest_rising:       { icon: Rocket,  gradient: "from-[#00D4FF] to-[#9D4EDD]",                     tone: "text-nexus-blue", label: "Fastest Rising" },
  best_ugc:             { icon: Camera,  gradient: "from-[#9D4EDD] to-[#FF107A]",                     tone: "text-nexus-pink", label: "Best UGC Creator" },
  highest_earning:      { icon: Wallet,  gradient: "from-[#FFEA00] to-[#FF107A]",                     tone: "text-[#FFEA00]", label: "Highest Earning" },
};

function toAbsolute(u) {
  if (!u) return null;
  return u.startsWith("http") ? u : `${BACKEND_URL}${u}`;
}

function formatValue(w) {
  if (w.category === "highest_earning") return `£${Number(w.metric_value).toLocaleString()}`;
  if (w.category === "best_ugc") return `${Number(w.metric_value).toFixed(1)} ★`;
  return Number(w.metric_value).toLocaleString();
}

function formatMonthLabel(month) {
  try {
    const [y, m] = month.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  } catch { return month; }
}

function WinnerCard({ winner, size = "md" }) {
  const meta = CATEGORY_META[winner.category] || CATEGORY_META.creator_of_the_month;
  const Icon = meta.icon;
  const big = size === "lg";
  return (
    <Link
      to={`/creators/${winner.user_id}`}
      className={`relative overflow-hidden rounded-3xl hairline bg-[#0C0C10] card-hover beam-hover flex flex-col ${big ? "p-8" : "p-6"}`}
      data-testid={`hof-card-${winner.category}`}
    >
      <div className={`absolute -top-16 -right-16 h-40 w-40 rounded-full bg-gradient-to-br ${meta.gradient} opacity-30 blur-3xl pointer-events-none`} />
      <div className="flex items-center justify-between mb-6 relative">
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-2">
          <Icon className={`h-4 w-4 ${meta.tone}`} /> {meta.label}
        </div>
        <div className={`h-9 w-9 rounded-full flex items-center justify-center bg-gradient-to-br ${meta.gradient} shadow-[0_0_20px_rgba(157,78,221,0.5)]`}>
          <Crown className="h-4 w-4 text-white" />
        </div>
      </div>
      <div className="flex items-center gap-4 relative">
        <div className={`rounded-2xl overflow-hidden hairline bg-black/40 shrink-0 ${big ? "h-24 w-24" : "h-16 w-16"}`}>
          {winner.avatar_url ? (
            <img src={toAbsolute(winner.avatar_url)} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-3xl font-display font-bold nexus-gradient-text">
              {winner.name?.[0]?.toUpperCase()}
            </div>
          )}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className={`font-display font-bold tracking-tight ${big ? "text-2xl" : "text-xl"}`}>
              {winner.name}
            </div>
            <BadgeTier tier={winner.badge_tier || "bronze"} size="sm" />
          </div>
          {winner.tagline && !big ? null : (
            winner.tagline && <div className="text-xs text-zinc-400 mt-0.5">{winner.tagline}</div>
          )}
        </div>
      </div>
      <div className="mt-5 flex items-baseline justify-between relative">
        <div>
          <div className={`font-display font-black nexus-gradient-text leading-none ${big ? "text-5xl" : "text-3xl"}`}>
            {formatValue(winner)}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{winner.metric_label}</div>
        </div>
      </div>
    </Link>
  );
}

export default function HallOfFame() {
  const { user } = useAuth();
  const [current, setCurrent] = useState(null);
  const [archive, setArchive] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, a] = await Promise.all([
        api.get("/hall-of-fame/current"),
        api.get("/hall-of-fame"),
      ]);
      setCurrent(c.data);
      setArchive(a.data);
    } catch {
      toast.error("Failed to load Hall of Fame");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const snapshot = async () => {
    if (!user) { toast.error("Log in to snapshot"); return; }
    setBusy(true);
    try {
      const monthNow = new Date().toISOString().slice(0, 7);
      await api.post(`/hall-of-fame/snapshot?month=${monthNow}`);
      toast.success("Snapshotted current month — winners pinned to profiles");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const currentWinners = current?.winners || [];
  const monthLabel = current ? formatMonthLabel(current.month) : "";

  return (
    <div className="min-h-screen grain">
      <SEO
        title="Hall of Fame · Monthly Creator Winners"
        description="Meet the monthly winners on Nexus — Creator of the Month, Fastest Rising, Best UGC Creator and Highest Earning. Permanent badges pinned to every winning profile."
        path="/hall-of-fame"
        keywords={["creator of the month", "top influencers", "creator awards", "best UGC creator", "influencer of the month", "top rated creators"]}
      />
      <Navbar />

      <section className="relative overflow-hidden px-6 lg:px-10 pt-14 pb-12 border-b border-white/5">
        <div className="absolute -top-32 left-1/3 h-96 w-96 rounded-full bg-nexus-pink/15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 right-1/4 h-96 w-96 rounded-full bg-nexus-blue/15 blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto relative">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3 flex items-center gap-2">
            <Trophy className="h-3.5 w-3.5 text-[#FFEA00]" /> Hall of Fame
          </div>
          <div className="flex items-end justify-between flex-wrap gap-6">
            <h1 className="font-display font-bold text-5xl md:text-6xl tracking-tighter leading-[0.95] max-w-3xl">
              The creators <span className="nexus-gradient-text">making history</span>{" "}
              on Nexus.
            </h1>
            {user && (
              <Button
                onClick={snapshot}
                disabled={busy}
                variant="outline"
                className="bg-white/5 border-white/15 text-white hover:bg-white/10 hover:text-white rounded-full"
                data-testid="hof-snapshot-btn"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                {busy ? "Snapshotting…" : "Snapshot current month"}
              </Button>
            )}
          </div>
          <p className="text-zinc-400 mt-4 max-w-xl">
            Monthly winners pinned with permanent badges on their profile. Ranked by
            XP earned, ratings received, and money actually paid out this month.
          </p>
        </div>
      </section>

      <section className="px-6 lg:px-10 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-baseline justify-between mb-8">
            <h2 className="font-display font-bold text-3xl tracking-tight">
              {monthLabel || "This month"}{" "}
              <span className="text-zinc-500 text-lg font-normal ml-2">· Live standings</span>
            </h2>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-56 rounded-3xl bg-[#0C0C10] hairline animate-pulse" />
              ))}
            </div>
          ) : currentWinners.length === 0 ? (
            <div className="glass rounded-3xl p-14 text-center" data-testid="hof-current-empty">
              <Trophy className="h-8 w-8 mx-auto mb-3 text-zinc-500" />
              <div className="font-display text-2xl mb-2">No winners yet this month</div>
              <p className="text-zinc-400">
                As creators earn XP, get reviewed and complete hires, they&apos;ll surface here in real time.
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="hof-current-grid">
              {currentWinners.map((w) => (
                <WinnerCard key={w.category} winner={w} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="px-6 lg:px-10 py-12 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-baseline justify-between mb-8">
            <h2 className="font-display font-bold text-3xl tracking-tight">
              Past winners <span className="text-zinc-500 text-lg font-normal ml-2">· Archive</span>
            </h2>
          </div>

          {archive.length === 0 ? (
            <div className="glass rounded-3xl p-10 text-center" data-testid="hof-archive-empty">
              <div className="text-zinc-400 text-sm max-w-md mx-auto">
                No months snapshotted yet. On the first day of each month, winners get pinned to their profiles forever.
                {user && " Try the snapshot button above to preview it now."}
              </div>
            </div>
          ) : (
            <div className="space-y-10" data-testid="hof-archive">
              {archive.map((entry) => (
                <div key={entry.month}>
                  <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">
                    {formatMonthLabel(entry.month)}
                  </div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {entry.winners.map((w) => (
                      <WinnerCard key={w.id} winner={w} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
